<template>
  <div class="content-area">
    <div class="content-area-content">
      <p class="content-area-content-title">
        Promosyon Kodu ile Kampanyaya Katıl
      </p>
      <p class="content-area-content-description">
        Size özel tanımlanan promosyon kodunu girerek ilgili kategori komisyon
        indirimi kampanyasına kolayca katılabilirsiniz.
      </p>
    </div>
    <div class="content-area-input-wrapper">
      <Input
        id="promotion_code"
        v-model:value="promotionCode"
        label="Promosyon Kodu"
        type="text"
      />
      <AppButton
        primary
        md
        :loading="isSubmitting"
        :disabled="isSubmitting"
        title="Katıl"
        @click="confirmJoin"
      />
    </div>

    <Modal
      ref="errorModalRef"
      title=""
      :show-icon="false"
      :click-to-close="true"
      with-closer
      :modal-style="{ width: '420px', padding: '0' }"
      @on-positive-action-click="closeErrorModal"
    >
      <template #body>
        <div class="promo-modal">
          <div class="promo-modal-icon">
            <Icons
              name="FilledInfoCircle"
              color="#4FA4E5"
              size="lg-md"
            />
          </div>
          <h3 class="promo-modal-title">
            {{ errorTitle }}
          </h3>
          <p class="promo-modal-subtitle">
            {{ errorMessage }}
          </p>
          <button
            class="promo-modal-button"
            @click="onErrorButtonClick"
          >
            {{ errorButtonText }}
          </button>
        </div>
      </template>
    </Modal>

    <ModalJoinCampaign
      ref="joinModalRef"
      @join-success="onJoinedCampaign"
      @join-dismiss="onJoinDismissCampaign"
      @join-close="onJoinCloseClickCampaign"
    />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { Input, AppButton, Modal, Icons } from '@n11/so-ui-components';
import {
  mapCampaignToGA4Params,
  sendCategoryCommissionCampaignEvent,
  sendCategoryCommissionPromotionCodeJoinEvent,
  sendCategoryCommissionCampaignFailEvent,
} from '@/utils/ga4/helper';
import { GA4_EVENT } from '@/utils/ga4/event';
import { useCategoryCommissionCampaignPromoCodeDetail } from './services';
import ModalJoinCampaign from './partials/modals/ModalJoinCampaign';

const router = useRouter();
const store = useStore();
const { isSubmitting, fetchCampaignDetailByPromoCode } =
  useCategoryCommissionCampaignPromoCodeDetail();
const emit = defineEmits(['join-success']);

const sellerInfo = computed(() => ({
  sellerId: store.state.auth.user.sellerId || '',
  sellerName: store.state.auth.user.username || '',
}));

const promotionCode = ref('');
const lastPromotionCode = ref('');

const errorModalRef = ref(null);
const joinModalRef = ref(null);
const errorTitle = ref('');
const errorMessage = ref('');
const errorCode = ref(null);
const errorCampaignId = ref(null);
const campaignDetailData = ref(null);

const getPagePath = () => {
  return typeof window !== 'undefined' ? window.location.pathname : '';
};

const errorButtonText = computed(() => {
  const buttonMap = {
    'SELLER_COMMISSION_CAMPAIGN.alreadyJoined': 'Kampanyaya Git',
    'SELLER_COMMISSION_CAMPAIGN.campaignExpired': 'Komisyon Kampanyalarına Git',
  };
  return buttonMap[errorCode.value] || 'Tamam';
});

const closeErrorModal = () => {
  errorModalRef.value?.hiddenModal?.();
};

const sendJoinCampaignEvent = (eventName) => {
  const ga4Payload = mapCampaignToGA4Params(
    campaignDetailData.value || {},
    sellerInfo.value,
  );
  delete ga4Payload.campaign_id;

  sendCategoryCommissionCampaignEvent(eventName, {
    ...ga4Payload,
    page_type: 'Kategori Komisyon Kampanyaları',
    tab: 'Promosyon Kodu',
    promotion_code: lastPromotionCode.value,
  });
};

const onJoinedCampaign = () => {
  sendJoinCampaignEvent(GA4_EVENT.JOIN_CAMPAIGN_SUCCESS_COMISSION);
  emit('join-success');
};

const onJoinDismissCampaign = () => {
  sendJoinCampaignEvent(GA4_EVENT.JOIN_CAMPAIGN_CANCEL_CLICK);
};

const onJoinCloseClickCampaign = () => {
  sendJoinCampaignEvent(GA4_EVENT.JOIN_CAMPAIGN_CLOSE_CLICK);
};

const onErrorButtonClick = () => {
  const actions = {
    'SELLER_COMMISSION_CAMPAIGN.alreadyJoined': () =>
      router.push(
        `/campaign/categoryCommission/joined/${errorCampaignId.value}`,
      ),
  };

  const action = actions[errorCode.value];
  action ? action() : closeErrorModal();
};

const confirmJoin = async () => {
  if (isSubmitting.value) return;

  if (!promotionCode.value?.trim()) {
    errorTitle.value = 'Hata';
    errorMessage.value = 'Lütfen geçerli bir promosyon kodu giriniz.';
    errorModalRef.value?.openModal?.();
    return;
  }

  sendCategoryCommissionPromotionCodeJoinEvent({
    page_path: getPagePath(),
    page_type: 'Kategori Komisyon Kampanyaları',
    tab: 'Promosyon Kodu',
    promotion_code: promotionCode.value.trim(),
    seller_name: sellerInfo.value.sellerName,
    seller_id: sellerInfo.value.sellerId,
  });

  lastPromotionCode.value = promotionCode.value.trim();
  const result = await fetchCampaignDetailByPromoCode(promotionCode.value);

  if (result.success) {
    campaignDetailData.value = {
      ...result.data,
      joinContext: {
        type: 'promoCode',
        value: lastPromotionCode.value,
      },
    };
    promotionCode.value = '';
    errorCode.value = null;
    errorCampaignId.value = null;
    joinModalRef.value?.open?.(campaignDetailData.value, {
      page_type: 'Kategori Komisyon Kampanyaları',
      tab: 'Promosyon Kodu',
      promotion_code: lastPromotionCode.value,
      seller_name: sellerInfo.value.sellerName,
      seller_id: sellerInfo.value.sellerId,
    });
  } else {
    sendCategoryCommissionCampaignFailEvent({
      page_type: 'Kategori Komisyon Kampanyaları',
      fail_cause: result.error.message || '',
      promotion_code: lastPromotionCode.value,
      seller_name: sellerInfo.value.sellerName,
      seller_id: sellerInfo.value.sellerId,
    });
    errorTitle.value = result.error.title;
    errorMessage.value = result.error.message;
    errorCode.value = result.error.code;
    errorCampaignId.value = result.error.campaignId;
    errorModalRef.value?.openModal?.();
  }
};
</script>

<style scoped lang="scss">
@import '@/assets/styles/variables';

.content-area {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  justify-content: flex-start;
  gap: 16px;
  border-radius: 8px;
  background: $colorN00;
  padding: 16px 24px;

  .content-area-content {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;

    .content-area-content-title {
      color: $colorN80;
      font-size: 16px;
      font-weight: 700;
      line-height: 20px;
      letter-spacing: -0.05px;
    }

    .content-area-content-description {
      color: $colorN80;
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
      letter-spacing: -0.05px;
    }
  }

  .content-area-input-wrapper {
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    justify-content: flex-start;
    gap: 16px;
    flex: 1;
    border-radius: 8px;
    border: 1px solid $colorB20;
    background: $colorB05;
    padding: 12px;

    :deep(.input-wrapper) {
      width: 513px;
    }

    :deep(.btn) {
      width: 73px;
    }
  }
}

.promo-modal {
  display: flex;
  padding: 20px;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  .promo-modal-icon {
    display: flex;
    justify-content: center;
  }

  .promo-modal-title {
    margin: 0;
    align-self: stretch;
    color: $colorN80;
    text-align: center;
    font-size: 20px;
    font-style: normal;
    font-weight: 700;
    line-height: 26px;
    letter-spacing: -0.2px;
  }

  .promo-modal-subtitle {
    margin: 0;
    color: $colorN80;
    text-align: center;
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: -0.05px;
  }

  .promo-modal-button {
    display: flex;
    height: 48px;
    padding: 14px 24px;
    justify-content: center;
    align-items: center;
    gap: 4px;
    width: 100%;
    border-radius: 6px;
    background: $colorN80;
    color: $colorN00;
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 20px;
    letter-spacing: -0.05px;
    border: none;
    cursor: pointer;
  }
}
</style>
